import { useCallback, useEffect, useRef, useState } from 'react';

// Partie CEEB: wysyłka ZBIORCZA (2026-09-01) i akceptacja ZBIORCZA (2026-10-09) — ten sam kształt
// (start → jobId → status running|done|error), więc jeden hook z wstrzykniętym API zamiast dwóch kopii.
// Wzorzec 1:1 z `useProtocolPdfsPolling` — kanon
// pollingu w tym projekcie (CLAUDE.md zakazuje nowego inline `setInterval` w komponentach;
// ten w `handlePushToCeeb` jest długiem, nie wzorcem).
//
// Limit czasu jest CELOWO wysoki: partia jest SEKWENCYJNA (wszystkie boty dzielą jedną sesję
// CEEB), a pojedynczy budynek potrafi schodzić kilkanaście minut. Zbyt krótki timeout ubijałby
// UI w połowie poprawnie działającej partii.
export type BatchUiStatus = 'idle' | 'running' | 'done' | 'error';

export interface CeebBatchApi<S> {
  start: (buildingIds: string[]) => Promise<{ jobId: string }>;
  status: (jobId: string) => Promise<S>;
}

type BatchResult = { status: string; error?: string };

export interface UseCeebBatchPolling<S extends BatchResult> {
  status: BatchUiStatus;
  result: S | null;
  jobId: string | null;
  start: (buildingIds: string[]) => Promise<void>;
  reset: () => void;
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_MS = 3 * 60 * 60 * 1000; // 3 h — limit backendu to 120 min (wysyłka) / 30 min (akceptacja) NA BUDYNEK

/** `api` musi być stabilne (stała modułu) — jest zależnością `start`. */
export function useCeebBatchPolling<S extends BatchResult>(api: CeebBatchApi<S>): UseCeebBatchPolling<S> {
  const [status, setStatus] = useState<BatchUiStatus>('idle');
  const [result, setResult] = useState<S | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const pollRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const jobRef = useRef<string | null>(null);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => clearPoll, [clearPoll]);

  const reset = useCallback(() => {
    clearPoll();
    jobRef.current = null;
    setJobId(null);
    setResult(null);
    setStatus('idle');
  }, [clearPoll]);

  const start = useCallback(async (buildingIds: string[]) => {
    clearPoll();
    setStatus('running');
    setResult(null);
    try {
      const { jobId: id } = await api.start(buildingIds);
      jobRef.current = id;
      setJobId(id);
      startedAtRef.current = Date.now();
      pollRef.current = window.setInterval(async () => {
        // Guard anty-stale: reaguj wyłącznie na status WŁASNEGO joba.
        if (jobRef.current !== id) return;
        if (Date.now() - startedAtRef.current > MAX_POLL_MS) {
          clearPoll();
          setStatus('error');
          setResult({ status: 'error', error: 'Przekroczono czas oczekiwania na partię' } as S);
          return;
        }
        try {
          const s = await api.status(id);
          setResult(s);
          if (s.status === 'done' || s.status === 'error') {
            clearPoll();
            setStatus(s.status as BatchUiStatus);
          }
        } catch (_) {
          // pojedynczy nieudany poll ignorujemy — timeout łapie resztę
        }
      }, POLL_INTERVAL_MS);
    } catch (e) {
      clearPoll();
      setStatus('error');
      setResult({ status: 'error', error: e instanceof Error ? e.message : 'Nie udało się wystartować' } as S);
    }
  }, [clearPoll, api]);

  return { status, result, jobId, start, reset };
}
